import React, { useState, useRef, useEffect } from 'react';
import { PlayCircleOutlined, PauseOutlined } from '@ant-design/icons';

interface AudioPlayerProps {
	src: string;
	isOwnMessage?: boolean;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({ src, isOwnMessage = false }) => {
	const [isPlaying, setIsPlaying] = useState(false);
	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(0);
	const [waveformData, setWaveformData] = useState<number[]>([]);
	const [isLoading, setIsLoading] = useState(true);

	const audioRef = useRef<HTMLAudioElement | null>(null);
	const canvasRef = useRef<HTMLCanvasElement | null>(null);
	const animationFrameRef = useRef<number | null>(null);
	const audioContextRef = useRef<AudioContext | null>(null);

	// Initialize audio element
	useEffect(() => {
		if (!audioRef.current) {
			audioRef.current = new Audio(src);
		} else {
			audioRef.current.src = src;
		}

		const audio = audioRef.current;

		const handleLoadedMetadata = () => {
			setDuration(audio.duration);
			setIsLoading(false);
		};

		const handleTimeUpdate = () => {
			setCurrentTime(audio.currentTime);
		};

		const handleEnded = () => {
			setIsPlaying(false);
			setCurrentTime(0);
		};

		const handleError = () => {
			setIsLoading(false);
		};

		audio.addEventListener('loadedmetadata', handleLoadedMetadata);
		audio.addEventListener('timeupdate', handleTimeUpdate);
		audio.addEventListener('ended', handleEnded);
		audio.addEventListener('error', handleError);

		// Generate waveform data
		generateWaveform(src);

		return () => {
			audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
			audio.removeEventListener('timeupdate', handleTimeUpdate);
			audio.removeEventListener('ended', handleEnded);
			audio.removeEventListener('error', handleError);
		};
	}, [src]);

	// Cleanup on unmount
	useEffect(() => {
		const animationFrame = animationFrameRef.current;
		const audioContext = audioContextRef.current;
		const audio = audioRef.current;

		return () => {
			if (animationFrame) {
				cancelAnimationFrame(animationFrame);
			}
			if (audioContext) {
				audioContext.close();
			}
			if (audio) {
				audio.pause();
				audioRef.current = null;
			}
		};
	}, []);

	// Generate waveform data using Web Audio API
	const generateWaveform = async (audioSrc: string) => {
		try {
			const response = await fetch(audioSrc);
			const arrayBuffer = await response.arrayBuffer();

			const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
			audioContextRef.current = audioContext;

			const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
			const rawData = audioBuffer.getChannelData(0); // Get first channel
			const samples = 100; // Number of bars in waveform
			const blockSize = Math.floor(rawData.length / samples);
			const filteredData = [];

			for (let i = 0; i < samples; i++) {
				let sum = 0;
				for (let j = 0; j < blockSize; j++) {
					sum += Math.abs(rawData[i * blockSize + j]);
				}
				filteredData.push(sum / blockSize);
			}

			// Normalize data
			const max = Math.max(...filteredData);
			// Guard against division by zero when all samples are silent
			const normalizedData =
				max > 0 ? filteredData.map((n) => (n / max) * 100) : filteredData.map(() => 0);
			setWaveformData(normalizedData);
			setIsLoading(false);
		} catch (error) {
			console.error('Error generating waveform:', error);
			// Fallback: create dummy waveform data
			const dummyData = Array(100)
				.fill(0)
				.map(() => Math.random() * 30 + 20);
			setWaveformData(dummyData);
			setIsLoading(false);
		}
	};

	// Draw waveform on canvas
	useEffect(() => {
		if (!canvasRef.current || waveformData.length === 0) return;

		const canvas = canvasRef.current;
		const ctx = canvas.getContext('2d');
		if (!ctx) return;

		const width = canvas.width;
		const height = canvas.height;
		const barWidth = width / waveformData.length;
		const progress = duration > 0 ? currentTime / duration : 0;
		const progressX = width * progress;

		// Clear canvas
		ctx.clearRect(0, 0, width, height);

		// Draw waveform bars
		waveformData.forEach((value, index) => {
			const x = index * barWidth;
			const barHeight = (value / 100) * height;
			const y = (height - barHeight) / 2;

			// Color bars before progress as orange, after as gray
			if (x < progressX) {
				ctx.fillStyle = isOwnMessage ? 'rgba(255, 255, 255, 0.6)' : '#ff6b35';
			} else {
				ctx.fillStyle = isOwnMessage ? 'rgba(255, 255, 255, 0.3)' : '#9ca3af';
			}

			ctx.fillRect(x, y, barWidth - 1, barHeight);
		});

		// Draw progress line
		if (progressX > 0 && progressX < width) {
			ctx.strokeStyle = isOwnMessage ? 'rgba(255, 255, 255, 0.8)' : '#ff6b35';
			ctx.lineWidth = 2;
			ctx.beginPath();
			ctx.moveTo(progressX, 0);
			ctx.lineTo(progressX, height);
			ctx.stroke();
		}
	}, [waveformData, currentTime, duration, isOwnMessage]);

	// Handle play/pause
	const togglePlayPause = () => {
		if (!audioRef.current) return;

		if (isPlaying) {
			audioRef.current.pause();
		} else {
			audioRef.current.play();
		}
		setIsPlaying(!isPlaying);
	};

	// Handle waveform click (seek)
	const handleWaveformClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
		if (!audioRef.current || !canvasRef.current || duration === 0) return;

		const canvas = canvasRef.current;
		const rect = canvas.getBoundingClientRect();
		const x = e.clientX - rect.left;
		const clickPercent = x / canvas.width;
		const newTime = clickPercent * duration;

		audioRef.current.currentTime = newTime;
		setCurrentTime(newTime);
	};

	// Format time as MM:SS
	const formatTime = (seconds: number) => {
		if (isNaN(seconds)) return '0:00';
		const mins = Math.floor(seconds / 60);
		const secs = Math.floor(seconds % 60);
		return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
	};

	// Choose dynamic classes for color
	const bgClass = isOwnMessage ? 'bg-transparent' : 'bg-gray-200 bg-opacity-50';
	const durationClass = isOwnMessage ? 'text-white text-opacity-70' : 'text-gray-400';

	return (
		<div className={`flex min-w-[200px] items-center gap-2 rounded-xl py-2 ${bgClass}`}>
			{/* Play/Pause Button */}
			<button
				onClick={togglePlayPause}
				className="flex h-8 w-8 flex-shrink-0 cursor-pointer items-center justify-center border-none bg-transparent p-0 outline-none"
				type="button"
			>
				{isPlaying ? (
					<PauseOutlined
						style={{
							fontSize: 24,
							color: isOwnMessage ? 'white' : '#000',
						}}
					/>
				) : (
					<PlayCircleOutlined
						style={{
							fontSize: 24,
							color: isOwnMessage ? 'white' : '#000',
						}}
					/>
				)}
			</button>

			{/* Waveform Canvas */}
			<div className="relative h-10 min-w-[120px] max-w-[200px] flex-1">
				{isLoading ? (
					<div
						className={`flex h-full items-center justify-center ${
							isOwnMessage ? 'text-white text-opacity-60' : 'text-gray-400'
						}`}
					>
						Loading...
					</div>
				) : (
					<canvas
						ref={canvasRef}
						width={200}
						height={40}
						onClick={handleWaveformClick}
						className="block h-full w-full cursor-pointer"
					/>
				)}
			</div>

			<div className={`min-w-[35px] flex-shrink-0 text-right text-xs ${durationClass}`}>
				{formatTime(duration)}
			</div>
		</div>
	);
};

export default AudioPlayer;
